import { gerPersonalizedProductDetails } from 'api/product/getPersonalizedProductDetails';
import {
  PersonalDiscountedVariant,
  PersonalizedProductDetailsResponse,
  PersonalProductDetailsInput,
  SegmentationInput,
} from 'api/product/getPersonalizedProductDetails/types';
import { PetContextValues } from 'contexts/pet';
import { PetProfile } from 'contexts/pet-profile';
import { SegmentationStore } from 'contexts/segmentation';
import { debounce } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import {
  generateInputFromPetProfile,
  generateInputFromSegmentation,
} from 'utils/api/smartCollection';
import { checkSegmentationMatch } from 'utils/segmentation/check-segmentation-match';

type Input = {
  petProfile?: PetProfile;
  segmentation?: SegmentationStore;
  productId: string;
  couponCode?: string | null;
  defaultPriceVariants?: PersonalDiscountedVariant[];
  isReady: boolean;
  multiSegmentMode: boolean;
  currentPetType: PetContextValues;
  interval?: string;
  variantId?: string;
} & {
  ninetailed?: PersonalProductDetailsInput['ninetailed'];
  audiences?: PersonalProductDetailsInput['audiences'];
};

type PartialOutput = {
  variants?: PersonalDiscountedVariant[];
  couponDefinition: undefined;
};

const textKey: keyof PersonalizedProductDetailsResponse = 'recommendation_text';

/** Same as variant D in DUX test  */
const EC_CUSTOMER_WORKAROUND_IDENTIFIER = '5DFTkfeIFDQeE9pyEe0rs3.3';

export function isPartialOutput(arg: any): arg is PartialOutput {
  return typeof arg === 'object' && typeof arg[textKey] === 'undefined';
}

type SWRKey = [
  Input['productId'],
  Input['segmentation'],
  Input['couponCode'],
  Input['ninetailed'],
  Input['audiences'],
  Input['isReady'],
  Input['multiSegmentMode'],
  Input['currentPetType'],
  Input['petProfile'],
  Input['interval'],
  Input['variantId'],
];

/**
 * Hook to fetch personalized product information
 * would it be okay if we check the header values is updated?
 */
const usePersonalizedProductDetails = ({
  segmentation,
  productId,
  couponCode,
  defaultPriceVariants,
  isReady,
  ninetailed,
  audiences,
  multiSegmentMode,
  currentPetType,
  petProfile,
  interval,
  variantId,
}: Input) => {
  const [currentData, setCurrentData] = useState<
    PersonalizedProductDetailsResponse | PartialOutput | null
  >({ variants: defaultPriceVariants, couponDefinition: undefined });

  const debouncedNinetailed = useRef(ninetailed);

  const debounceNinetailedUpdate = debounce((value) => {
    debouncedNinetailed.current = value;
  }, 250);

  useEffect(() => {
    debounceNinetailedUpdate(ninetailed);
  }, [ninetailed, debounceNinetailedUpdate]);

  const debouncedAudience = useRef(audiences);

  const debounceAudienceUpdate = debounce((value) => {
    debouncedAudience.current = value;
  }, 250);

  useEffect(() => {
    debounceAudienceUpdate(audiences);
  }, [ninetailed, debounceNinetailedUpdate, debounceAudienceUpdate, audiences]);

  const { data, isValidating } = useSWR(
    () => {
      return [
        productId,
        segmentation,
        couponCode,
        debouncedNinetailed.current,
        debouncedAudience.current,
        isReady,
        multiSegmentMode,
        currentPetType,
        petProfile,
        interval,
        variantId,
      ] as SWRKey;
    },
    ([
      productId,
      segmentation,
      couponCode,
      ninetailed,
      audiences,
      isReady,
      multiSegmentMode,
      currentPetType,
      petProfile,
      interval, // eslint-disable-line @typescript-eslint/no-unused-vars -- Used for cache differentiation only
      variantId, // eslint-disable-line @typescript-eslint/no-unused-vars -- Used for cache differentiation only
    ]) => {
      let segmentInput: undefined | SegmentationInput = undefined;
      if (isReady) {
        if (
          !multiSegmentMode &&
          segmentation?.lastSegmentation &&
          checkSegmentationMatch({
            currentPetType,
            segmentation: segmentation.lastSegmentation,
          })
        ) {
          segmentInput = generateInputFromSegmentation({
            segmentation: segmentation?.lastSegmentation,
          });
        } else if (multiSegmentMode && petProfile) {
          segmentInput = generateInputFromPetProfile({
            petProfile,
            omitFoodType: true,
          });
        }

        if (segmentInput && Object.keys(segmentInput).length === 0) {
          segmentInput = undefined;
        }
      }

      return gerPersonalizedProductDetails({
        product_id: productId,
        coupon_code: couponCode,
        segmentation: segmentInput,
        /**
         * @TODO : Workaround to avoid the consistent output for EC customer
         * @SEE : https://petsdeli.atlassian.net/browse/PT-7659
         * */
        ninetailed: [...(ninetailed ?? []), EC_CUSTOMER_WORKAROUND_IDENTIFIER],
        audiences,
      });
    },
    {
      onSuccess: (data) => {
        setCurrentData(data);
      },
    }
  );

  return {
    personalizedProductDetails: data ?? currentData,
    isLoading: isValidating,
  };
};

export default usePersonalizedProductDetails;
